import { useAuth } from '@/context/AuthContext';

/**
 * PermissionGate - Conditionally render children based on user permissions
 * 
 * Props:
 * - permission: string - The permission key to check (e.g., 'send_estimates')
 * - permissions: string[] - Array of permissions (any one grants access)
 * - requireAll: boolean - If true with permissions array, all must be true
 * - fallback: ReactNode - What to render if permission denied (default: null)
 * - children: ReactNode - What to render if permission granted
 * 
 * Usage:
 * <PermissionGate permission="send_estimates">
 *   <Button>Send Estimate</Button>
 * </PermissionGate>
 * 
 * <PermissionGate permissions={['create_invoices', 'send_invoices']} requireAll>
 *   <Button>Create & Send Invoice</Button>
 * </PermissionGate>
 */
export const PermissionGate = ({ 
  permission, 
  permissions, 
  requireAll = false, 
  fallback = null, 
  children 
}) => {
  const { hasPermission, user } = useAuth();
  
  // Admins and owners always have access
  if (user?.role === 'admin' || user?.role === 'owner') {
    return children;
  }
  
  let hasAccess = false;
  
  if (permission) {
    // Single permission check
    hasAccess = hasPermission(permission);
  } else if (permissions && Array.isArray(permissions)) {
    // Multiple permissions check
    if (requireAll) {
      hasAccess = permissions.every(p => hasPermission(p));
    } else {
      hasAccess = permissions.some(p => hasPermission(p));
    }
  }
  
  return hasAccess ? children : fallback;
};

/**
 * usePermission - Hook to check single or multiple permissions
 * 
 * Usage:
 * const canSendEstimate = usePermission('send_estimates');
 * const canManageInvoices = usePermission(['create_invoices', 'send_invoices']);
 */
export const usePermission = (permissionOrArray, requireAll = false) => {
  const { hasPermission, user } = useAuth();
  
  // Admins and owners always have access
  if (user?.role === 'admin' || user?.role === 'owner') {
    return true;
  }
  
  if (typeof permissionOrArray === 'string') {
    return hasPermission(permissionOrArray);
  }
  
  if (Array.isArray(permissionOrArray)) {
    if (requireAll) {
      return permissionOrArray.every(p => hasPermission(p));
    }
    return permissionOrArray.some(p => hasPermission(p));
  }
  
  return false;
};

/**
 * Permission keys reference:
 * 
 * Jobs & Site Visits:
 * - view_assigned_jobs
 * - view_assigned_site_visits
 * - update_job_status
 * - complete_site_visits
 * - create_jobs
 * - assign_crew
 * 
 * Notes & Media:
 * - add_notes
 * - add_photos
 * - add_sketches
 * - add_measurements
 * 
 * Estimates:
 * - create_draft_estimates
 * - edit_draft_estimates
 * - send_estimates
 * - view_all_estimates
 * 
 * Invoices & Payments:
 * - create_invoices
 * - send_invoices
 * - record_payments
 * 
 * Customers:
 * - view_customers
 * - create_customers
 * - archive_customers
 * 
 * Admin:
 * - manage_users
 * - access_settings
 * - manage_billing
 * - delete_records
 * - export_data
 * - view_reports
 * - manage_templates
 */

export default PermissionGate;
